import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocaleLink as Link } from "../../i18n/LocaleLink";
import { useFaceGate } from "./FaceGateContext";
import {
  WATCH_CHAPTERS,
  WATCH_CHAPTER_IDS,
  chapterForPathname,
  type JourneyChapter,
  type JourneyChapterId,
} from "../../lib/journey";
import "./watch-shell.css";

/**
 * ChapterNav — the Watch-tab bezel.
 *
 * S4 density pass: watch-shell.css tightens mark padding + name size at
 * ≤400px so all six marks stay readable at 320px without horizontal
 * scroll (names truncate rather than wrap).
 *
 * The public bezel draws the six current Watch chapters from the canonical
 * journey config. Legacy Journey/Predictions routes remain addressable but
 * are not promoted as competing primary Watch chapters.
 *
 * Pre-cast behavior (no chart id):
 *   - Only chapter 0 (Winding) is enabled.
 *   - Chart-bound chapters render as unavailable marks with
 *     `aria-disabled="true"` and a "Available after casting" tooltip.
 *   - No chapter silently falls back to the vesper anchor. The route
 *     builder returns /cast for chapters that need a chart but no id is
 *     present.
 *
 * With a chart:
 *   - All chapters render as enabled links.
 *   - The active chapter (from the current pathname) is marked
 *     `aria-current="page"`.
 *   - Completed chapters show a ✓ badge.
 */
interface Props {
  chartId?: string | null;
  /** Compact mode for tight headers (just marks, no labels). */
  compact?: boolean;
}

export function ChapterNav({ chartId, compact = false }: Props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = chapterForPathname(pathname);
  const gate = useFaceGate();
  const hasChart = !!chartId;

  // Task 4B — disabled-chapter accessibility rework.
  // Disabled chapters expose one shared explanation. Before this
  // fix, the bezel exposed `aria-disabled="true"` redundantly on
  // both the parent <li> and the inner <span>. That double-exposure
  // is also technically invalid — aria-disabled has no semantic
  // effect on <li> or plain <span> per ARIA 1.2.
  //
  // Replacement: a single shared explanatory <span id="chapter-nav-help">
  // in sr-only form, referenced via aria-describedby from each disabled
  // chapter. The visual .is-disabled class on the <li> is the
  // styling signal. The disabled semantic is communicated via the
  // descriptive text (announced by screen readers via the implicit
  // accessible name from .chapter-nav__name + the description).
  return (
    <nav className="chapter-nav" aria-label={t("ui.web.long_tail.watch.chapter_nav_aria") }>
      <span id="chapter-nav-help" className="sr-only">
        {t("ui.web.long_tail.watch.disabled_help")}
      </span>
      <ol className="chapter-nav__track">
        {WATCH_CHAPTERS.map((c) => (
          <ChapterMark
            key={c.id}
            chapter={c}
            chartId={chartId ?? null}
            isActive={c.id === active}
            isPassed={hasChart && active >= 0 && c.id < active}
            hasChart={hasChart}
            complete={hasChart && c.id !== 0 ? gate.isComplete(c.id as Exclude<JourneyChapterId, 0>) : false}
            compact={compact}
          />
        ))}
      </ol>
    </nav>
  );
}

interface ChapterMarkProps {
  chapter: JourneyChapter;
  chartId: string | null;
  isActive: boolean;
  isPassed: boolean;
  hasChart: boolean;
  complete: boolean;
  compact: boolean;
}

function ChapterMark({
  chapter,
  chartId,
  isActive,
  isPassed,
  hasChart,
  complete,
  compact,
}: ChapterMarkProps) {
  const { t } = useTranslation();
  const unavailable = !hasChart && chapter.needsChart;
  // Frozen IA: bezel shows plain position numbers (Chapter N of 6).
  // Roman marks survive only in journey.ts comments and unit tests.
  const position = WATCH_CHAPTER_IDS.indexOf(chapter.id) + 1;
  const eyebrow = t("ui.web.watch.header.eyebrow", {
    x: position,
    total: WATCH_CHAPTER_IDS.length,
  });
  const title = unavailable
    ? `${eyebrow} — ${chapter.name} · available after casting`
    : `${eyebrow} — ${chapter.name}${complete ? " · read" : ""}`;
  const href = chapter.routeBuilder(chartId);

  // Pre-cast: render a non-anchor so screen readers and keyboards skip
  // it. /cast stays the only interactive destination.
  //
  // Task 4B — disabled-state markup rework:
  //   - Both prior `aria-disabled="true"` attributes were redundant
  //     (the spec forbids aria-disabled on <li> and <span>).
  //   - tabIndex={-1} on a non-focusable <span> was no-op.
  //   - The .is-disabled class on the parent <li> is the visual signal.
  //   - The shared `chapter-nav-help` <span> (rendered above the list)
  //     describes the disabled state via `aria-describedby`, so
  //     screen readers announce the explanation once and reference
  //     it from each disabled chapter.
  if (unavailable) {
    return (
      <li
        className={`chapter-nav__mark ${isActive ? "is-active" : ""} is-disabled`}
        aria-describedby="chapter-nav-help"
        data-disabled="true"
      >
        <span
          className="chapter-nav__link"
          title={title}
          aria-describedby="chapter-nav-help"
        >
          <span className="chapter-nav__num" aria-hidden="true">
            {position}
          </span>
          {!compact && <span className="chapter-nav__name">{chapter.name}</span>}
        </span>
      </li>
    );
  }

  return (
    <li
      className={[
        "chapter-nav__mark",
        isActive ? "is-active" : "",
        isPassed ? "is-passed" : "",
        complete ? "is-complete" : "",
      ].join(" ").trim()}
    >
      <Link
        to={href}
        className="chapter-nav__link"
        title={title}
        aria-current={isActive ? "page" : undefined}
      >
        <span className="chapter-nav__num" aria-hidden="true">
          {complete ? "✓" : position}
        </span>
        {!compact && <span className="chapter-nav__name">{chapter.name}</span>}
      </Link>
    </li>
  );
}

export default ChapterNav;
